import { useState, useEffect } from 'react';
import { getFeaturesStatus, FeatureStatus, isFeatureEnabled } from '../services/features';
import { authService } from '../services/auth';
import {
  loadStoredFeatures,
  saveStoredFeatures,
  getFeatureStorageKey,
} from '../services/featureStorage';

export interface UseFeaturesReturn {
  features: FeatureStatus[];
  enabledFeatures: string[];
  isLoading: boolean;
  isVerified: boolean;
  error: Error | null;
  isFeatureEnabled: (featureKey: string) => boolean;
  refetch: () => Promise<void>;
}

interface UseFeaturesOptions {
  enabled?: boolean;
}

interface FeaturesCache {
  features: FeatureStatus[];
  enabledFeatures: string[];
  fetchedAt: number;
  verified: boolean;
}

let featuresCache: FeaturesCache | null = null;
let inFlightRequest: Promise<FeaturesCache> | null = null;
let currentFeatureStorageKey: string | null = null;

const syncMemoryCacheWithStorage = () => {
  const user = authService.getUser();
  const nextKey = getFeatureStorageKey(user);
  if (currentFeatureStorageKey !== nextKey) {
    currentFeatureStorageKey = nextKey;
    const stored = loadStoredFeatures(user);
    featuresCache = stored
      ? {
          ...stored,
          verified: false,
        }
      : null;
  }
};

/**
 * Hook to fetch and manage feature status for the current tenant
 * Call this on app load to determine which menu items to show
 */
export function useFeatures(options?: UseFeaturesOptions): UseFeaturesReturn {
  const isEnabled = options?.enabled ?? true;
  syncMemoryCacheWithStorage();
  const hasInitialCache = Boolean(isEnabled && featuresCache);
  const [features, setFeatures] = useState<FeatureStatus[]>(
    hasInitialCache && featuresCache ? featuresCache.features : []
  );
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>(
    hasInitialCache && featuresCache ? featuresCache.enabledFeatures : []
  );
  const [isLoading, setIsLoading] = useState(Boolean(isEnabled && !hasInitialCache));
  const [isVerified, setIsVerified] = useState(Boolean(hasInitialCache && featuresCache?.verified));
  const [error, setError] = useState<Error | null>(null);

  const fetchFeatures = async (forceRefresh = false) => {
    if (!isEnabled) {
      setFeatures([]);
      setEnabledFeatures([]);
      setIsLoading(false);
      setIsVerified(false);
      return;
    }

    try {
      syncMemoryCacheWithStorage();
      if (!forceRefresh && featuresCache?.verified) {
        setFeatures(featuresCache.features);
        setEnabledFeatures(featuresCache.enabledFeatures);
        setIsLoading(false);
        setIsVerified(true);
        return;
      }

      // Only show loading spinner when there is no data to render yet.
      const hasAnyFeatures = features.length > 0 || enabledFeatures.length > 0;
      setIsLoading(!hasAnyFeatures);
      setError(null);

      if (!inFlightRequest || forceRefresh) {
        inFlightRequest = (async () => {
          const data = await getFeaturesStatus();
          const safeFeatures = Array.isArray(data.features) ? data.features : [];
          const safeEnabled = Array.isArray(data.enabledFeatures) ? data.enabledFeatures : [];
          const nextCache: FeaturesCache = {
            features: safeFeatures,
            enabledFeatures: safeEnabled,
            fetchedAt: Date.now(),
            verified: true,
          };
          featuresCache = nextCache;
          saveStoredFeatures(nextCache, authService.getUser());
          return nextCache;
        })();
      }

      const cached = await inFlightRequest;
      setFeatures(cached.features);
      setEnabledFeatures(cached.enabledFeatures);
      setIsVerified(Boolean(cached.verified));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch features'));
      console.error('[useFeatures] Error fetching features:', err);
      // On error, ensure we still have empty arrays
      setFeatures([]);
      setEnabledFeatures([]);
    } finally {
      inFlightRequest = null;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEnabled) {
      setFeatures([]);
      setEnabledFeatures([]);
      setIsLoading(false);
      setIsVerified(false);
      return;
    }
    fetchFeatures();
  }, [isEnabled]);

  const checkFeature = (featureKey: string): boolean => {
    return isFeatureEnabled(featureKey, enabledFeatures);
  };

  return {
    features,
    enabledFeatures,
    isLoading,
    isVerified,
    error,
    isFeatureEnabled: checkFeature,
    refetch: () => fetchFeatures(true),
  };
}


